
import { useState, useRef } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import PagarPedidoContainer from '../../../../../../containers/pagarPedido/pagarPedidoContainer';
import { GerarPixResponse } from '../../../../../../types/lotePedido/gerarPixResponse';
import * as S from './adicionarSaldoPixStyles';
import { formatToBRL } from 'base/src/utils/mathUtil';
import { LotePedidoResponse } from '../../../../../../types/lotePedido/lotePedidoResponse';

export type AdicionarSaldoPixProps = {
  lote: LotePedidoResponse;
  ValorTotal: string;
  loteId?: number;
  carteiraId: number;
  onClose?: () => void;
}

export function AdicionarSaldoPix(props: AdicionarSaldoPixProps) {
  const _pagarPedidoContainer = new PagarPedidoContainer();
  const [pixData, setPixData] = useState<GerarPixResponse | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isWaitingPayment, setIsWaitingPayment] = useState(false);
  const [isPaymentConfirmed, setIsPaymentConfirmed] = useState(false);
  const isPixCancelledRef = useRef(false);

  async function handleGerarPix() {
    try {
      setIsGenerating(true);

      const result = await _pagarPedidoContainer.gerarPix(
        props.lote.CampanhaPedidoResgatePagamentoID,
        props.carteiraId
      );

      if (result instanceof Error) {
        toast('Erro ao gerar PIX', { type: 'error' });
        return;
      }

      setPixData(result);
      setIsWaitingPayment(true);
      toast('PIX gerado com sucesso!', { type: 'success' });

      // DESATIVADO: Inicia o SSE para verificar o pagamento
      // _pagarPedidoContainer.pixCheckSSE(result.CodigoInterno, (success) => {
      //   setIsWaitingPayment(false);
      //   if (success) {
      //     setIsPaymentConfirmed(true);
      //     // Aqui você pode adicionar lógica adicional após a confirmação do pagamento
      //     // Por exemplo, fechar o modal, atualizar dados, etc.
      //   } else {
      //     // Só mostra erro se o PIX não foi cancelado pelo usuário
      //     if (!isPixCancelledRef.current) {
      //       toast('Erro na confirmação do pagamento', { type: 'error' });
      //     }
      //   }
      // });

    } catch (error) {
      toast('Erro ao gerar PIX', { type: 'error' });
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleCopyPixCode() {
    if (pixData?.PixCopiaCola) {
      try {
        await navigator.clipboard.writeText(pixData.PixCopiaCola);
        toast('Código PIX copiado!', { type: 'success' });
      } catch (error) {
        toast('Erro ao copiar código PIX', { type: 'error' });
      }
    }
  }

  function handleResetPix() {
    isPixCancelledRef.current = true;
    setPixData(null);
    setIsWaitingPayment(false);
    setIsPaymentConfirmed(false);
    _pagarPedidoContainer.resetPix();
    toast('PIX cancelado', { type: 'info' });

    // Reset o flag após um tempo para permitir novos PIXs
    setTimeout(() => {
      isPixCancelledRef.current = false;
    }, 1000);
  }

  return (
    <S.Container>

      {!isWaitingPayment && (
        <S.AvisoContainer>
          <S.AvisoTitle>
            <Icon icon="ph:warning" />
            Importante
          </S.AvisoTitle>
          <S.AvisoText>
            Após efetuar o pagamento via PIX, aguarde a confirmação automática do sistema.
            O processo pode levar alguns minutos.
          </S.AvisoText>
        </S.AvisoContainer>
      )}

      {isWaitingPayment && (
        <S.AvisoContainerWarning>
          <S.AvisoTextWarning>
            Realize o pagamento via PIX e aguarde a confirmação automática.
            O sistema irá notificar quando o pagamento for processado.
          </S.AvisoTextWarning>
          <S.CancelButtonContainer>
            <S.CancelButton onClick={handleResetPix}>
              <Icon icon="ph:x" />
              Cancelar PIX
            </S.CancelButton>
          </S.CancelButtonContainer>
        </S.AvisoContainerWarning>
      )}

      {!pixData && (
        <S.InfoSection>
          <S.SectionTitle>
            <Icon icon="ph:info" />
            Informações do Pagamento
          </S.SectionTitle>

          <S.InfoGrid>
            <S.InfoCard>
              <S.InfoLabel>Valor Total</S.InfoLabel>
              <S.InfoValue size="lg" color="success">
                {formatToBRL(props.ValorTotal)}
              </S.InfoValue>
            </S.InfoCard>

            <S.InfoCard>
              <S.InfoLabel>ID do Pagamento</S.InfoLabel>
              <S.InfoValue>
                {props.lote.CampanhaPedidoResgatePagamentoID}
              </S.InfoValue>
            </S.InfoCard>

            <S.InfoCard>
              <S.InfoLabel>Carteira ID</S.InfoLabel>
              <S.InfoValue>
                {props.carteiraId}
              </S.InfoValue>
            </S.InfoCard>
          </S.InfoGrid>
        </S.InfoSection>
      )}


      {!pixData && (
        <S.ComprovanteContainer>
          <S.ComprovanteButton
            onClick={handleGerarPix}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <Icon icon="ph:spinner" className="animate-spin" />
                Gerando PIX...
              </>
            ) : (
              <>
                <Icon icon="ph:qr-code" />
                Gerar PIX
              </>
            )}
          </S.ComprovanteButton>
        </S.ComprovanteContainer>
      )}

      {pixData && !isPaymentConfirmed && (
        <S.InfoSection>
          <S.SectionTitle>
            <Icon icon="ph:check-circle" color="#28a745" />
            PIX Gerado com Sucesso
          </S.SectionTitle>

          <S.InfoGrid>
            <S.InfoCard fullWidth>
              <S.InfoLabel>QR Code</S.InfoLabel>
              <S.QrCodeContainer>
                <S.QrCodeImage
                  src={`data:image/png;base64,${pixData.QrCode}`}
                  alt="QR Code PIX"
                />
              </S.QrCodeContainer>
            </S.InfoCard>

            <S.InfoCard fullWidth>
              <S.InfoLabel>Código PIX Copia e Cola</S.InfoLabel>
              <S.PixCodeContainer>
                <S.PixCodeValue>
                  {pixData.PixCopiaCola}
                </S.PixCodeValue>
                <S.CopyButton onClick={handleCopyPixCode}>
                  <Icon icon="ph:copy" />
                  Copiar
                </S.CopyButton>
              </S.PixCodeContainer>
            </S.InfoCard>
          </S.InfoGrid>
        </S.InfoSection>
      )}

      {isPaymentConfirmed && pixData && (
        <S.InfoSection>
          <S.PaymentConfirmedContainer>
            <S.SuccessIconContainer>
              <Icon icon="ph:check-circle-fill" />
            </S.SuccessIconContainer>

            <S.ConfirmedTitle>
              Pagamento Confirmado!
            </S.ConfirmedTitle>

            <S.ConfirmedText>
              Seu pagamento PIX foi processado com sucesso.
              <br />
              Valor: <strong>R$ {formatToBRL(pixData.PixValue)}</strong>
            </S.ConfirmedText>

            <S.ConfirmedInfoGrid>
              <S.InfoCard>
                <S.InfoLabel>Depósito ID</S.InfoLabel>
                <S.InfoValue color="success">
                  {pixData.DepositoID}
                </S.InfoValue>
              </S.InfoCard>

              <S.InfoCard>
                <S.InfoLabel>Código Interno</S.InfoLabel>
                <S.InfoValue color="success">
                  {pixData.CodigoInterno}
                </S.InfoValue>
              </S.InfoCard>
            </S.ConfirmedInfoGrid>

            <S.NewPixButtonContainer>
              <S.NewPixButton onClick={handleResetPix}>
                <Icon icon="ph:arrow-left" />
                Fazer Novo PIX
              </S.NewPixButton>
            </S.NewPixButtonContainer>
          </S.PaymentConfirmedContainer>
        </S.InfoSection>
      )}
    </S.Container>
  );
}

export default AdicionarSaldoPix;